<template>
  <div>
    <v-dialog :value="dialog" width="850" persistent scrollable>
      <v-card>
        <AdCardTitle>
          {{ $t('condition') }}
        </AdCardTitle>

        <v-card-text>
          <v-tabs v-model="activeTab">
            <v-tab v-if="textual">{{ $t('text-conditions') }}</v-tab>
            <v-tab v-if="structural">{{ $t('structure-conditions') }}</v-tab>
          </v-tabs>
          <v-tabs-items v-model="activeTab">
            <v-tab-item class="pt-5">
              <!-- Textual Conditions -->
              <AdInfoOwl>{{ $t('text-conditions-hint') }}</AdInfoOwl>
              <v-data-table
                :headers="headers"
                :items="items"
                :items-per-page="items.length"
                hide-default-footer
                disable-sort
                disable-filtering
                disable-pagination
              >
                <template v-slot:[`item.desc`]="{ item }">
                  <EditableTranslationEditor v-model="item.desc"/>
                </template>

                <template v-slot:[`item.cardinality`]="{ item }">
                  <EditableDropdown
                    :value="getDefaultCardinality(item.conformance, item.cardinality)"
                    :items="getPossibleCardinalities(item.conformance)"
                    @change="item.cardinality = $event"
                  />

                  <!-- shall use <EditableCardinality v-model="item.cardinality" scenario/> -->
                </template>

                <template v-slot:[`item.conformance`]="{ item }">
                  <EditableDropdown
                    v-model="item.conformance"
                    :items="conformanceItems"
                    @change="item.cardinality = getDefaultCardinality(item.conformance, item.cardinality)"
                  />
                  <!-- shall use <EditableConformance v-model="item.conformance" scenario/> -->
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                  <v-btn icon @click="deleteCondition(item)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </template>

                <template slot="no-data">
                  <AdInfoOwl>{{ $t('no-conditions-yet') }}</AdInfoOwl>
                </template>

              </v-data-table>
              <v-spacer></v-spacer>
              <AdButton
                type="add"
                :text="$t('condition')"
                @click="addCondition"
              />
            </v-tab-item>
            <v-tab-item class="pt-5">
              <!-- Structural Conditions -->
              <AdInfoOwl>{{ $t('structure-conditions-hint') }}</AdInfoOwl>
              <StructuralConditionEditor :conditions="enableWhenItems" :representingTemplate="representingTemplate">
                <template #condition-table-top>
                  <v-radio-group
                    :value="enableBehavior"
                    @change="$emit('update:enableBehavior', $event)"
                    hide-details
                    row
                    dense
                    mandatory
                    :label="$t('enable-behavior')"
                    class="mt-0 ml-4 mb-2"
                  >
                    <v-radio v-for="behavior in enableBehaviorTypes" :key="behavior.value" :label="behavior.value" :value="behavior.value"/>
                  </v-radio-group>
                </template>
              </StructuralConditionEditor>

            </v-tab-item>
          </v-tabs-items>

        </v-card-text>

        <v-card-actions>
          <v-spacer/>
          <AdButton
            type="cancel"
            :text="$t('cancel')"
            @click="closeDialog"
          />
          <AdButton
            type="ok"
            :text="$t('ok')"
            @click="saveConditions"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ConformanceCardinalityHelper from '@/components/Scenario/ConformanceCardinalityHelper'
import StructuralConditionEditor from '@/components/Utilities/StructuralConditionEditor'
import { cloneDeep } from 'lodash'

export default {
  components: {
    StructuralConditionEditor
  },
  mixins: [ConformanceCardinalityHelper],
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    conditions: {
      type: Array,
      required: false,
      default: () => []
    },
    enableWhen: {
      type: Array,
      required: false,
      default: () => []
    },
    enableBehavior: {
      type: String,
      required: false,
      default: 'all',
      validator: v => ['all', 'any'].includes(v)
    },
    textual: {
      type: Boolean,
      required: false,
      default: true
    },
    representingTemplate: {
      type: Array,
      default: () => []
    },
    structural: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  model: {
    prop: 'conditions',
    event: 'input'
  },

  data () {
    return {
      headers: [{
        value: 'desc',
        text: 'Description',
        width: '70%'
      }, {
        value: 'cardinality',
        text: 'Card',
        width: '10%'
      }, {
        value: 'conformance',
        text: 'Conf',
        width: '10%'
      }, {
        value: 'actions',
        text: '',
        width: '10%'
      }],
      items: [], // editable conditions
      enableWhenItems: [], // editable enableWhens
      cardinalityItems: ['', '0..0', '0..1', '0..*', '1..1', '1..*'],
      conformanceItems: ['', 'M', 'R', 'O', 'NP'],
      activeTab: 0
    }
  },
  computed: {
    enableBehaviorTypes () {
      return this.$store.getters['server/getDecorTypes']?.data?.EnableBehaviorType?.[0]?.enumeration || []
    },
    watchForInitItems () {
      return [this.conditions, this.dialog]
    },
    watchForInitEnableWhenItems () {
      return [this.enableWhen, this.dialog]
    }
  },

  watch: {
    watchForInitItems: {
      immediate: true,
      deep: true,
      handler ([conditions, dialog]) {
        // Check if conditions is not an array.
        if (Array.isArray(conditions) === false) {
          // Set empty items.
          this.items = []
          // Stop execution.
          return
        }

        // Set items.
        this.items = conditions.map((condition) => {
          const cardinality = this.$filters.getCardinality(condition)
          return {
            ...condition,
            cardinality
          }
        })
      }
    },
    watchForInitEnableWhenItems: {
      immediate: true,
      deep: true,
      handler ([enableWhens, dialog]) {
        this.enableWhenItems = cloneDeep(enableWhens)
      }
    }
  },

  methods: {
    closeDialog () {
      this.$emit('close')
    },

    addCondition () {
      this.items.push({
        desc: null,
        minimumMultiplicity: null,
        maximumMultiplicity: null,
        cardinality: null,
        conformance: null,
        isMandatory: false
      })
    },

    async deleteCondition (item) {
      const agree = await this.$confirm(this.$t('delete-condition-hint'))
      if (!agree) return

      // Try to get index of item.
      const index = this.items.indexOf(item)
      // Check if index is defined.
      if (index >= 0) {
        // Remove item from items array.
        this.items.splice(index, 1)
      }
    },

    /**
     * Save conditions.
     */
    saveConditions () {
      // Get parsed conditions.
      const conditions = this.items.slice().map((item) => {
        // Get cardinality.
        const cardinality = item.cardinality
          ? item.cardinality.split('..')
          : null

        // Return mapped structure.
        return {
          ...item,
          minimumMultiplicity: cardinality?.[0] || 0,
          maximumMultiplicity: cardinality?.[1] || 0,
          isMandatory: false,
          cardinality: undefined
        }
      })

      this.$emit('input', conditions)

      this.$emit('update:enableWhen', this.enableWhenItems)

      // Close dialog.
      this.closeDialog()
    }
  }
}
</script>
